// Update banner, countdown pill, and overlay switch for the signed-in agent
// shell (DSAF-01, DSAF-02, D-01 to D-04). The strip is in flow above the main
// scroll region (never fixed), collapses to a pill in the same slot, and is
// never hidden or dismissed. At the deadline, or when forced, it hands off to
// the blocking VersionOverlay. Public routes mount only QuietReloadWatcher,
// which renders nothing (D-15, D-16).
import React from 'react';
import { useVersionWatch, nowCorrected } from '../../hooks/useVersionWatch';
import { useQuietReload } from '../../hooks/useQuietReload';
import { pillMinutes, AUTO_EXPAND_AT_MS } from '../../utils/versionWatch';
import { setStashIdentity, clearStash, sweepStash } from '../../utils/unsavedWork';
import { isPublicRoutePath } from '../../config/publicRoutes';
import Button from './primitives/Button';
import IconButton from './primitives/IconButton';
import VersionOverlay from './VersionOverlay';

const ANNOUNCE_MINUTES = [15, 10, 5, 1];

export function versionModeForPath(pathname) {
    return isPublicRoutePath(pathname) ? 'quiet' : 'full';
}

export function QuietReloadWatcher({ reloadOnClientTooOld = false }) {
    useQuietReload({ reloadOnClientTooOld });
    return null;
}

export function useStashIdentitySync({ uid, workspaceId }) {
    const prevUid = React.useRef(null);
    React.useEffect(() => {
        setStashIdentity(uid ? { uid, workspaceId } : null);
        if (prevUid.current && !uid) clearStash();
        else sweepStash({ uid: uid || null });
        prevUid.current = uid || null;
    }, [uid, workspaceId]);
}

const svgProps = {
    width: 16,
    height: 16,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': 'true',
    focusable: 'false',
};

export default function VersionBanner() {
    const { status, deadline } = useVersionWatch();
    const [collapsed, setCollapsed] = React.useState(false);
    const [reloading, setReloading] = React.useState(false);
    const [, setTick] = React.useState(0);
    const [announce, setAnnounce] = React.useState('');
    const [entered, setEntered] = React.useState(false);
    const autoExpanded = React.useRef(false);
    const lastAnnounced = React.useRef(null);
    const focusTarget = React.useRef(null);
    const reloadRef = React.useRef(null);
    const pillRef = React.useRef(null);

    const showing = status === 'banner' && deadline !== null;

    React.useEffect(() => {
        if (!showing) return undefined;
        const id = setInterval(() => setTick((n) => n + 1), 1000);
        return () => clearInterval(id);
    }, [showing]);

    React.useEffect(() => { if (showing) setEntered(true); }, [showing]);

    const remaining = showing ? Math.max(0, deadline - nowCorrected()) : 0;
    const minutes = pillMinutes(remaining);
    const withinAutoExpand = remaining <= AUTO_EXPAND_AT_MS;

    React.useEffect(() => {
        if (!showing) return;
        if (!autoExpanded.current && withinAutoExpand) {
            autoExpanded.current = true;
            setCollapsed(false);
        }
        if (ANNOUNCE_MINUTES.includes(minutes) && lastAnnounced.current !== minutes) {
            lastAnnounced.current = minutes;
            setAnnounce(`KeyFlow will update in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}.`);
        }
    }, [showing, withinAutoExpand, minutes]);

    React.useLayoutEffect(() => {
        const target = focusTarget.current === 'reload' ? reloadRef.current : pillRef.current;
        if (focusTarget.current && target) target.focus();
        focusTarget.current = null;
    }, [collapsed]);

    // Keyed so a move into or out of lockout remounts the overlay: lockout never
    // counts down, and leaving it starts a fresh save wait and countdown.
    if (status === 'lockout') return <VersionOverlay key="lockout" mode="lockout" />;
    if (status === 'overlay') return <VersionOverlay key="reload" mode="timeout" />;
    if (status === 'forced') return <VersionOverlay key="reload" mode="forced" />;
    if (!showing) return null;
    if (reloading) return <VersionOverlay mode="timeout" immediate />;

    const collapse = () => { focusTarget.current = 'pill'; setCollapsed(true); };
    const expand = () => { focusTarget.current = 'reload'; setCollapsed(false); };

    return (
        <div
            role="region"
            aria-label="App update"
            className={`shrink-0 motion-safe:transition-all motion-safe:duration-200 ${entered ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'}`}
        >
            <span className="sr-only" aria-live="polite">{announce}</span>
            {collapsed ? (
                <div className="flex justify-end px-3 lg:px-4 py-1" style={{ minHeight: 36 }}>
                    <Button
                        ref={pillRef}
                        variant="secondary"
                        size="sm"
                        aria-expanded="false"
                        aria-label={`Update in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}. Show details.`}
                        onClick={expand}
                        className="gap-2 font-semibold tabular-nums"
                        style={{ height: 28, borderRadius: 9999 }}
                    >
                        <span
                            aria-hidden="true"
                            className="rounded-full"
                            style={{ width: 12, height: 12, backgroundColor: 'var(--color-warning)' }}
                        />
                        Update in {minutes} min
                    </Button>
                </div>
            ) : (
                <div
                    className="flex flex-wrap items-center gap-2 px-3 lg:px-4 border-b border-l-2 border-[color:var(--color-accent)]"
                    style={{
                        minHeight: 36,
                        backgroundColor: 'var(--surface-primary)',
                        borderBottomColor: 'var(--border-color)',
                        borderBottomWidth: 1,
                    }}
                >
                    <span style={{ color: 'var(--text-muted)' }}>
                        <svg {...svgProps}>
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 16v-4" />
                            <path d="M12 8h.01" />
                        </svg>
                    </span>
                    <div className="flex-1 min-w-0 text-sm" style={{ color: 'var(--text-primary)' }}>
                        <span role="status">A new version of KeyFlow is available.</span>
                        {' '}
                        <span aria-hidden="true" style={{ color: 'var(--text-muted)' }}>
                            It will update on its own in {minutes} min.
                        </span>
                    </div>
                    <Button ref={reloadRef} variant="primary" size="sm" onClick={() => setReloading(true)}>
                        Reload now
                    </Button>
                    <IconButton aria-label="Minimize update notice" onClick={collapse}>
                        <svg {...svgProps}><path d="m18 15-6-6-6 6" /></svg>
                    </IconButton>
                </div>
            )}
        </div>
    );
}
